import { connect } from "react-redux";
import _ from "lodash";
import React, { useState } from "react";
import classes from "./Table.scss";
import TableBody from "./Components/TableBody";
import { useI18n } from "Hooks";
import Loading from "UIComponents/Loading";
import { colors } from "Constants";
import { onAddFirstRow, getFilteredColumns } from "NodeEditor/Utils";
import { Tooltip } from "@toddle-edu/ds-web";
import DropDown from "./Components/DropDown";
import ACLStore from "lib/aclStore";
import {
  AddCircleOutlined,
  SettingsOutlined,
  WarningCircleOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import TagsSetupModal from "./Components/TableRow/Components/TagsSetupModal";
import { onEnterOrSpaceKeyDown } from "Utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

const disabledStyle = { fill: colors.strokeTwo, cursor: "not-allowed" };
const overlayStyle = { maxWidth: 180 };

const TableHeader = props => {
  const { t } = useI18n([
    "scopeAndSequence",
    "common",
    "academicSetup",
    "a11y",
  ]);
  const {
    mode,
    nodes,
    setNodes,
    rootNodeId,
    updateNodesInDraft,
    userId,
    currentEditorConfig,
    columnIds,
    isActionDisabled,
    plannerElementSet,
    organizationTags,
    inputForMultiLevelTagsQuery,
    nodesObjMap,
    setToastMsg,
    filterNodeIds,
  } = props;

  const [isChecked, setIsChecked] = useState(false);
  const [isTagsSetupModalOpen, setIsTagsSetupModal] = useState(false);

  const rootNodeChildren = _.get(nodes[rootNodeId], "children", []);
  const allowedColumns = currentEditorConfig.columns;
  if (_.isEmpty(rootNodeChildren) && isChecked) {
    setIsChecked(false);
  }
  const checkAllNodes = (nodeIds = null) => {
    const newNodes = { ...nodes };

    let targetNodeIds;
    if (nodeIds) {
      const isArray = _.isArray(nodeIds);
      targetNodeIds = isArray ? nodeIds : _.keys(nodeIds);
    } else {
      const allNodeKeys = _.keys(newNodes);
      targetNodeIds = allNodeKeys.filter(key => {
        const nodeDepth = _.get(newNodes[key], "depth");
        return nodeDepth !== 0;
      });
    }

    _.forEach(targetNodeIds, key => {
      const node = newNodes[key];
      const nodeDepth = _.get(node, "depth");
      if (node && nodeDepth !== 0) {
        newNodes[key] = { ...node, isChecked: true };
      }
    });

    setNodes(newNodes);
    setIsChecked(true);
  };

  const handleSelectNone = (nodeIds = null) => {
    const newNodes = { ...nodes };

    let targetNodeIds;
    if (nodeIds) {
      const isArray = _.isArray(nodeIds);
      targetNodeIds = isArray ? nodeIds : _.keys(nodeIds);
    } else {
      const allNodeKeys = _.keys(newNodes);
      targetNodeIds = allNodeKeys.filter(key => {
        const nodeDepth = _.get(newNodes[key], "depth");
        return nodeDepth !== 0;
      });
    }

    _.forEach(targetNodeIds, key => {
      const node = newNodes[key];
      newNodes[key] = { ...node, isChecked: false };
    });

    setNodes(newNodes);
    setIsChecked(false);
  };
  const checkIsValid = (nodeIds = null) => {
    handleSelectNone(nodeIds);
    let isValid = true;
    const errorCheckFields = _.get(currentEditorConfig, "errorCheckFields", []);
    let nodeToScrollTo;
    const newNodes = { ...nodesObjMap };
    _.forOwn(newNodes, (value, key) => {
      if (value.parentId) {
        for (const field of errorCheckFields) {
          const currentFieldValue = value[field];
          if (_.isEmpty(currentFieldValue) && value.depth != 0) {
            newNodes[key] = { ...value, isError: true, isChecked: true };
            isValid = false;
            if (!nodeToScrollTo) nodeToScrollTo = key;
            break;
          }
          newNodes[key] = { ...value, isError: false, isChecked: false };
        }
      }
    });
    if (isValid) {
      setToastMsg({
        type: "success",
        msg: "toastMsgs:no_error_cells_found",
      });
      return true;
    } else {
      setNodes(newNodes);
      const node = document.getElementById(nodeToScrollTo);
      if (node) node.scrollIntoView({ block: "center" });
      return false;
    }
  };

  const handleSelectAllErrors = (nodeIds = null) => {
    checkIsValid(nodeIds);
  };

  const filteredColumns = getFilteredColumns({ allowedColumns, mode });

  const tooltip = (
    <ul className={classes.benchmarkQuestionTooltipContainer}>
      <li>{t("academicSetup:benchmark_question_tooltip_label1")}</li>
      <li>{t("academicSetup:benchmark_question_tooltip_label2")}</li>
    </ul>
  );

  const handleAddFirstRow = () => {
    const updatedNodes = onAddFirstRow({ rootNodeId, nodes, setNodes, userId });
    updateNodesInDraft({ updatedNodes });
  };

  const addIconTooltipText = t("common:add_with_label", {
    label: t("scopeAndSequence:strand"),
  });

  const showTagsSettingsIcon =
    ACLStore.can("FeatureFlag:EnableSNSTagConfiguration") && mode === "edit";

  const handleSettingIconClick = () => {
    setIsTagsSetupModal(true);
  };

  const handleSettingIconKeyDown = e => {
    onEnterOrSpaceKeyDown(e, () => {
      e.stopPropagation();
      handleSettingIconClick();
    });
  };

  const handleAddFirstRowKeyDown = e => {
    onEnterOrSpaceKeyDown(e, () => {
      e.stopPropagation();
      handleAddFirstRow();
    });
  };

  return (
    <>
      <thead className={classes.tableHeader}>
        <tr>
          {_.map(filteredColumns, column => {
            const columnLabel = _.get(column, "label", "");
            const columnStyle = _.get(column, "style", {});
            const columHeader = _.get(
              plannerElementSet,
              "label",
              t(columnLabel)
            );
            switch (column.id) {
              case columnIds.label: {
                return (
                  <th
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <div className={classes.learningStandardsHeader}>
                      {mode === "edit" && (
                        <div className={classes.actionButtonContainer}>
                          <div
                            className={classes.addIconContainer}
                            tabIndex={!isActionDisabled ? 0 : -1}
                            onKeyDown={handleAddFirstRowKeyDown}
                          >
                            <div
                              className={classes.addIcon}
                              onClick={handleAddFirstRow}
                              style={isActionDisabled ? disabledStyle : {}}
                            >
                              <Tooltip
                                tooltip={addIconTooltipText}
                                placement={"top"}
                              >
                                <span
                                  className={classes.addIconTooltip}
                                  tabIndex={!isActionDisabled ? 0 : -1}
                                  onKeyDown={handleAddFirstRowKeyDown}
                                >
                                  <AddCircleOutlined size={"xx-small"} />
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                          <div className={classes.checkBoxContainer}>
                            <DropDown
                              onSelectAll={checkAllNodes}
                              onSelectNone={handleSelectNone}
                              onSelectAllErrors={handleSelectAllErrors}
                              nodes={nodes}
                              onClick={e => e.stopPropagation()}
                              filterNodeIds={filterNodeIds}
                            />
                          </div>
                        </div>
                      )}
                      <div>{columHeader}</div>
                    </div>
                  </th>
                );
              }
              case columnIds.subText:
              case columnIds.code: {
                return (
                  <th
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    {t(columnLabel)}
                  </th>
                );
              }
              case columnIds.grade: {
                return (
                  <th
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    {t(columnLabel)}
                  </th>
                );
              }
              case columnIds.tag: {
                return (
                  <th
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <div
                      className={classNames({
                        [classes.tagTextIconContainer]: mode === "edit",
                      })}
                    >
                      <div>{t(columnLabel)}</div>
                      {showTagsSettingsIcon ? (
                        <div
                          className={classes.settingsIconContainer}
                          onClick={handleSettingIconClick}
                          tabIndex={showTagsSettingsIcon ? 0 : -1}
                          onKeyDown={handleSettingIconKeyDown}
                        >
                          <SettingsOutlined size={"xx-small"} variant="on" />
                        </div>
                      ) : null}
                    </div>
                  </th>
                );
              }
              case columnIds.starQues: {
                return (
                  <th
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                    aria-label={t(columnLabel)}
                  >
                    <div className={classes.staarQuesContainer}>
                      <div className={classes.questionHeader}>
                        {t(columnLabel)}
                      </div>
                      <div className={classes.quesWrapper}>
                        <Tooltip
                          tooltip={tooltip}
                          placement={"top"}
                          id="tooltip-CreatePostSvg"
                          overlayStyle={overlayStyle}
                        >
                          <span tabIndex={0} className={classes.warningQues}>
                            <WarningCircleOutlined
                              variant={"link"}
                              size={"x-small"}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </th>
                );
              }
            }
          })}
        </tr>
      </thead>
      {isTagsSetupModalOpen ? (
        <TagsSetupModal
          onClose={() => setIsTagsSetupModal(false)}
          organizationTags={organizationTags}
          inputForMultiLevelTagsQuery={inputForMultiLevelTagsQuery}
          nodes={nodes}
          setNodes={setNodes}
          updateNodesInDraft={updateNodesInDraft}
        />
      ) : null}
    </>
  );
};

const Table = props => {
  const {
    mode,
    nodes,
    setNodes,
    rootNodeId,
    updateNodesInDraft,
    userId,
    currentEditorConfig,
    columnIds,
    isActionDisabled,
    plannerElementSet,
    nodeType,
    organizationTags,
    inputForMultiLevelTagsQuery,
    nodesObjMap,
    setToastMsg,
    filterNodeIds,
  } = props;
  const tableStyle = currentEditorConfig.tableStyle || {};

  return (
    <div className={classes.tableContainer}>
      {_.size(nodes) > 0 ? (
        <table className={classes.table} style={tableStyle} id={"TABLE"}>
          <TableHeader
            mode={mode}
            nodes={nodes}
            setNodes={setNodes}
            rootNodeId={rootNodeId}
            updateNodesInDraft={updateNodesInDraft}
            userId={userId}
            currentEditorConfig={currentEditorConfig}
            columnIds={columnIds}
            isActionDisabled={isActionDisabled}
            plannerElementSet={plannerElementSet}
            nodeType={nodeType}
            organizationTags={organizationTags}
            inputForMultiLevelTagsQuery={inputForMultiLevelTagsQuery}
            nodesObjMap={nodesObjMap}
            setToastMsg={setToastMsg}
            filterNodeIds={filterNodeIds}
          />
          <TableBody {...props} />
        </table>
      ) : (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      )}
    </div>
  );
};

const mapActionCreators = {
  setToastMsg,
};

export default connect(null, mapActionCreators)(Table);
